import {
  MAX_SUPERADMINS,
  MODULES,
  accessSummary,
  canManageUser,
  canRequestHandoff,
  effectiveLevels,
  isEmailAllowed,
  isStaffSurface,
  leastPrivilegedGroup,
  levelsFromGroups,
  personaOf,
  seatsUsed,
  superadmins,
  userStatusBreakdown,
  type AccessReviewStatus,
  type AuditEntry,
  type TenantUser,
  type UserStatus,
} from '@titan/shared';
import { SHIPPED_MODULES } from '@titan/modules';
import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSession } from '../../session/SessionProvider.js';
import {
  Actions,
  Button,
  Card,
  ChipSelector,
  ConfirmButton,
  EmptyState,
  InlineError,
  InlineInfo,
  ListContainer,
  ListHeader,
  ListRow,
  MultiChipSelector,
  Muted,
  Pill,
  PillRow,
  PrimaryCell,
  RowMeta,
  RowTitle,
  SecondaryCell,
  TextInput,
  WarningPanel,
  userStatusTone,
} from '../../ui/index.js';
import { errorMessage } from '../useLoad.js';
import type { AccessCenterProps } from './AccessCenter.js';
import { ApprovalsCard } from './ApprovalsCard.js';
import { useApprovalNotice } from './useNotice.js';
import styles from './Access.module.css';

type Filter = 'All' | UserStatus;

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function dateLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

interface Shared {
  q: string;
  busy: boolean;
  run: (work: () => Promise<unknown>) => Promise<void>;
}

/** People: approvals, review, the superadmins, the invite bar, the list, one ficha per person. */
export function PeopleTab(props: AccessCenterProps) {
  const { tenant, users, groups, roles, approvals, accessReview, actor, staffTenantId, canWrite, onChanged } = props;
  const { api } = useSession();
  const q = staffTenantId ? `?tenantId=${encodeURIComponent(staffTenantId)}` : '';
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('All');
  const [openId, setOpenId] = useState<string | null>(null);
  const { notice, check, clear } = useApprovalNotice();

  const others = users.filter((u) => !u.isSuperadmin);
  const breakdown = userStatusBreakdown(users);
  const visible = others.filter((u) => {
    if (filter !== 'All' && u.status !== filter) return false;
    const s = search.trim().toLowerCase();
    return !s || u.name.toLowerCase().includes(s) || u.email.includes(s);
  });

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    clear();
    try {
      check(await work());
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.stack}>
      {error && <InlineError>{error}</InlineError>}
      {notice && <InlineInfo>{notice}</InlineInfo>}

      <ApprovalsCard approvals={approvals} actor={actor} users={users} onChanged={onChanged} />
      {accessReview && <AccessReviewBanner review={accessReview} users={users} actor={actor} q={q} busy={busy} run={run} />}
      <SuperadminsCard {...props} q={q} busy={busy} run={run} />

      <ListContainer>
        <ListHeader title={`People at ${tenant.name}`} meta={`${breakdown.label} · ${seatsUsed(users)} of ${tenant.seats} seats`} />
        {canWrite && <InviteBar tenant={tenant} users={users} groups={groups} roles={roles} q={q} busy={busy} run={run} />}
        {others.length > 5 && (
          <div className={styles.toolbar}>
            <TextInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email" aria-label="Search people" />
            <ChipSelector<Filter>
              aria-label="Filter by status"
              options={[
                { value: 'All', label: 'All' },
                { value: 'Active', label: 'Active' },
                { value: 'Invited', label: 'Invited' },
                { value: 'Suspended', label: 'Suspended' },
              ]}
              value={filter}
              onChange={setFilter}
            />
          </div>
        )}
        {others.length === 0 && <EmptyState>Nobody besides the superadmins has been invited yet.{canWrite ? ' Send the first invitation above.' : ''}</EmptyState>}
        {others.length > 0 && visible.length === 0 && <EmptyState>Nobody matches that search.</EmptyState>}
        {visible.map((u) => (
          <PersonRow key={u.id} user={u} open={openId === u.id} onToggle={() => setOpenId(openId === u.id ? null : u.id)} {...props} q={q} busy={busy} run={run} />
        ))}
      </ListContainer>
    </div>
  );
}

function AccessReviewBanner({ review, users, actor, q, busy, run }: Shared & { review: AccessReviewStatus; users: TenantUser[]; actor: AccessCenterProps['actor'] }) {
  const { api } = useSession();
  const canComplete = canRequestHandoff(actor) || isStaffSurface(actor);
  const active = users.filter((u) => u.status === 'Active').length;
  if (!review.overdue && !review.dueSoon) {
    return (
      <Muted small>
        Last access review: {review.lastCompletedAt ? `${dateLabel(review.lastCompletedAt)} by ${review.completedBy ?? ''}` : 'none yet'} · next due {dateLabel(review.dueAt)}.
      </Muted>
    );
  }
  return (
    <Card variant={review.overdue ? 'warning' : 'tinted'} title={review.overdue ? 'Access review overdue' : 'Access review due soon'} description={`Every 90 days the superadmins confirm that the people list is right. ${active} active ${active === 1 ? 'person has' : 'people have'} access today. ${review.overdue ? 'It was due' : 'It is due'} on ${dateLabel(review.dueAt)}.`}>
      {canComplete ? (
        <Actions>
          <ConfirmButton
            label="Confirm the people list is right"
            variant="primary"
            size="md"
            disabled={busy}
            message={`You confirm that the ${active} active ${active === 1 ? 'person' : 'people'} at this organization should still have access, and that everyone who left has been suspended or removed. The confirmation is recorded in the audit trail.`}
            confirmLabel="Confirm review"
            onConfirm={() => run(() => api(`/org/access-review/complete${q}`, { method: 'POST' }))}
          />
        </Actions>
      ) : (
        <Muted small>A superadmin completes the review.</Muted>
      )}
    </Card>
  );
}

function SuperadminsCard({ tenant, users, actor, canWrite, q, busy, run }: AccessCenterProps & Shared) {
  const { api } = useSession();
  const [appointTo, setAppointTo] = useState<string | null>(null);
  const [handoffTo, setHandoffTo] = useState<string | null>(null);
  const [handoffSent, setHandoffSent] = useState<string | null>(null);
  const owners = superadmins(users);
  const staff = isStaffSurface(actor);
  const persona = personaOf(actor);
  const isOwner = canRequestHandoff(actor);
  const canGovern = isOwner || staff;
  const candidates = users.filter((u) => !u.isSuperadmin && u.status === 'Active');

  const appoint = () => run(() => api(`/org/superadmin/appoint${q}`, { method: 'POST', body: JSON.stringify({ userId: appointTo }) })).then(() => setAppointTo(null));
  const remove = (u: TenantUser) => run(() => api(`/org/superadmin/remove${q}`, { method: 'POST', body: JSON.stringify({ userId: u.id }) }));
  const handoff = () =>
    run(async () => {
      const res = await api<{ to: { email: string } }>(`/org/superadmin/handoff${q}`, { method: 'POST', body: JSON.stringify({ toUserId: handoffTo }) });
      setHandoffSent(res.to.email);
      setHandoffTo(null);
      return res;
    });

  return (
    <Card
      variant="tinted"
      title={owners.length === 1 ? 'Superadmin' : 'Superadmins'}
      description={`Up to ${MAX_SUPERADMINS} accountable people per organization: one in charge, one as backup. Full access to every module and to management. Appointing or removing one needs a second person’s approval.`}
    >
      {owners.length === 0 && <Muted>This organization has no superadmin. Ask DinoCloud to appoint one.</Muted>}
      {owners.map((sa) => (
        <div key={sa.id} className={styles.superadmin}>
          <div className={styles.superadminText}>
            <span className={styles.superadminName}>{sa.name}</span>
            <RowMeta>{sa.email}</RowMeta>
            <PillRow>
              <Pill tone={userStatusTone(sa.status)}>{sa.status}</Pill>
              <Muted small>{sa.last}</Muted>
              {persona.kind === 'tenant' && persona.userId === sa.id && <Pill tone="neutral">you</Pill>}
            </PillRow>
            {sa.status === 'Invited' && staff && <Muted small>Impersonation becomes available once they sign in for the first time.</Muted>}
          </div>
          <div className={styles.actionsWrap}>
            {sa.status === 'Invited' && canWrite && (
              <Button variant="secondary" size="sm" disabled={busy} onClick={() => run(() => api(`/org/users/${sa.id}/resend${q}`, { method: 'POST' }))}>
                Resend invitation
              </Button>
            )}
            {canGovern && owners.length > 1 && (
              <ConfirmButton
                label="Remove as superadmin"
                tone="critical"
                disabled={busy}
                message={`${sa.name} keeps their account and groups but stops being a superadmin. A second person has to approve this.`}
                confirmLabel="Ask for approval"
                onConfirm={() => remove(sa)}
              />
            )}
          </div>
        </div>
      ))}

      {canGovern && owners.length < MAX_SUPERADMINS && (
        <div className={styles.detailBlock}>
          <span className={styles.detailTitle}>Appoint a backup superadmin</span>
          {candidates.length === 0 ? (
            <Muted small>Nobody else at {tenant.name} has signed in yet, so there is nobody to appoint.</Muted>
          ) : (
            <>
              <ChipSelector options={candidates.map((u) => ({ value: u.id, label: `${u.name} · ${u.email}` }))} value={appointTo} onChange={setAppointTo} aria-label="Backup superadmin" />
              <Actions>
                <ConfirmButton
                  label="Appoint"
                  variant="primary"
                  disabled={!appointTo || busy}
                  message={`${candidates.find((c) => c.id === appointTo)?.name ?? 'This person'} gets full access to everything, including management, and can approve sensitive changes. A second person has to approve the appointment.`}
                  confirmLabel="Ask for approval"
                  onConfirm={appoint}
                />
              </Actions>
            </>
          )}
        </div>
      )}

      {isOwner && (
        <div className={styles.detailBlock}>
          <span className={styles.detailTitle}>Hand your role to someone else</span>
          {handoffSent && <InlineInfo>Confirmation sent to {persona.email}. Open the link within 24 hours to complete the handoff to {handoffSent}.</InlineInfo>}
          {candidates.length === 0 ? (
            <Muted small>Nobody else has signed in yet, so there is nobody to hand the role to.</Muted>
          ) : (
            <>
              <ChipSelector options={candidates.map((u) => ({ value: u.id, label: `${u.name} · ${u.email}` }))} value={handoffTo} onChange={setHandoffTo} aria-label="New superadmin" />
              <Actions>
                <ConfirmButton
                  label="Send confirmation to my email"
                  variant="destructive"
                  tone="critical"
                  disabled={!handoffTo || busy}
                  message={`A confirmation link goes to ${persona.email}. When you confirm it, ${candidates.find((c) => c.id === handoffTo)?.name ?? 'the chosen person'} becomes a superadmin and you stop being one; you keep your account and groups.`}
                  confirmLabel="Send the link"
                  onConfirm={handoff}
                />
              </Actions>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function InviteBar({ tenant, users, groups, roles, q, busy, run }: Shared & Pick<AccessCenterProps, 'tenant' | 'users' | 'groups' | 'roles'>) {
  const { api } = useSession();
  // Default to the least-privileged group that does not grant management, so an ordinary
  // invitation never needs approval by accident.
  const defaultGroup = useMemo(
    () => leastPrivilegedGroup(groups.filter((g) => levelsFromGroups([g.id], groups, roles).management !== 'full'), roles),
    [groups, roles],
  );
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [groupIds, setGroupIds] = useState<string[]>(defaultGroup ? [defaultGroup.id] : []);
  const emailOk = isEmailAllowed(email, tenant.allowedDomains);
  const seatsFull = seatsUsed(users) >= tenant.seats;
  const summary = accessSummary(levelsFromGroups(groupIds, groups, roles), SHIPPED_MODULES);

  const invite = () =>
    run(async () => {
      const res = await api(`/org/users${q}`, { method: 'POST', body: JSON.stringify({ email: email.trim(), name: name.trim(), groupIds }) });
      setEmail('');
      setName('');
      setGroupIds(defaultGroup ? [defaultGroup.id] : []);
      return res;
    });

  return (
    <div className={styles.bar}>
      <div className={styles.barRow}>
        <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={`name@${tenant.allowedDomains[0] ?? 'company.com'}`} aria-label="Email to invite" invalid={email.length > 0 && !emailOk} />
        <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" aria-label="Name" />
        <Button onClick={invite} disabled={!emailOk || !name.trim() || busy || seatsFull}>
          Send invitation
        </Button>
      </div>
      <MultiChipSelector aria-label="Groups" options={groups.map((g) => ({ value: g.id, label: g.name }))} values={groupIds} onChange={setGroupIds} />
      <span className={styles.note}>
        {groupIds.length === 0
          ? 'No group selected: this person signs in but sees nothing until you add them to a group.'
          : summary.full.length === 0 && summary.read.length === 0
            ? summary.management === 'full'
              ? 'These groups grant management: this person can invite people and change groups and roles.'
              : SHIPPED_MODULES.length === 0
                ? 'No product module has shipped yet. This person can sign in and will see modules as they ship.'
                : 'These groups grant no module yet. This person signs in but sees nothing.'
            : `They will get ${[
                summary.full.length ? `full access to ${summary.full.map((k) => MODULES[k].label).join(', ')}` : '',
                summary.read.length ? `read-only access to ${summary.read.map((k) => MODULES[k].label).join(', ')}` : '',
              ]
                .filter(Boolean)
                .join(' and ')}, limited to the modules the organization has.`}
        {tenant.ssoLabel ? ` They sign in with ${tenant.ssoLabel}; there is no password.` : ''}
      </span>
      {summary.management === 'full' && (
        <WarningPanel>This person will be able to invite people and change groups and roles for everyone except the superadmins. The invitation waits for a second person’s approval.</WarningPanel>
      )}
      {seatsFull && <span className={styles.note}>Every one of the {tenant.seats} contracted seats is in use. Adding a seat goes through your CSM.</span>}
      {email.length > 0 && !emailOk && <span className={styles.note}>Invitations go to {tenant.allowedDomains.join(', ')} addresses only.</span>}
    </div>
  );
}

function PersonRow({ user, open, onToggle, tenant, groups, roles, audit, actor, q, canWrite, busy, run }: AccessCenterProps & Shared & { user: TenantUser; open: boolean; onToggle: () => void }) {
  const { api } = useSession();
  const [draftGroups, setDraftGroups] = useState<string[] | null>(null);
  const manageable = canWrite && canManageUser(actor, user);
  const persona = personaOf(actor);
  const isSelf = persona.kind === 'tenant' && persona.userId === user.id;
  const levels = effectiveLevels({ user, groups, roles, entitlements: tenant.modules });
  const summary = accessSummary(levels, SHIPPED_MODULES);
  const groupNames = user.groupIds.map((id) => groups.find((g) => g.id === id)?.name).filter(Boolean).join(', ');
  const activity = audit.filter((a) => a.actorEmail === user.email || a.summary.includes(user.email)).slice(0, 5);
  const groupsDraft = draftGroups ?? user.groupIds;
  const groupsChanged = draftGroups !== null && (draftGroups.length !== user.groupIds.length || draftGroups.some((g) => !user.groupIds.includes(g)));
  const draftGrantsManagement = levelsFromGroups(groupsDraft, groups, roles).management === 'full' && levels.management !== 'full';

  const summaryText =
    user.status !== 'Active'
      ? user.status === 'Invited'
        ? `No access until they sign in · ${groupNames || 'no groups'}`
        : 'No access while suspended'
      : summary.full.length === 0 && summary.read.length === 0 && summary.management === 'none'
        ? SHIPPED_MODULES.length === 0
          ? 'Signs in; modules appear as they ship'
          : 'No access: not in any group that grants a module'
        : [
            summary.full.length ? `Full: ${summary.full.map((k) => MODULES[k].label).join(', ')}` : '',
            summary.read.length ? `Read only: ${summary.read.map((k) => MODULES[k].label).join(', ')}` : '',
            summary.management === 'full' ? 'Manages people' : summary.management === 'read' ? 'Sees people' : '',
          ]
            .filter(Boolean)
            .join(' · ');

  const patch = (body: Record<string, unknown>) => run(() => api(`/org/users/${user.id}${q}`, { method: 'PATCH', body: JSON.stringify(body) }));

  return (
    <>
      <ListRow center clickable onClick={onToggle} onKeyDown={(e) => e.key === 'Enter' && onToggle()} aria-expanded={open}>
        <PrimaryCell>
          <RowTitle small>{user.name}</RowTitle>
          <RowMeta>{user.email}</RowMeta>
        </PrimaryCell>
        <SecondaryCell size="xl">
          <span className={styles.summaryText}>{summaryText}</span>
        </SecondaryCell>
        <SecondaryCell>
          <PillRow>
            <Pill tone={userStatusTone(user.status)}>{user.status}</Pill>
          </PillRow>
        </SecondaryCell>
        <SecondaryCell size="lg">
          <RowMeta>{user.last}</RowMeta>
        </SecondaryCell>
        <ChevronDown size={15} strokeWidth={1.75} aria-hidden="true" className={[styles.chevron, open ? styles.chevronOpen : ''].join(' ')} />
      </ListRow>
      {open && (
        <div className={styles.detail}>
          <div className={styles.detailBlock}>
            <span className={styles.detailTitle}>Groups</span>
            {groups.length === 0 ? (
              <Muted small>No groups exist yet.</Muted>
            ) : (
              <MultiChipSelector aria-label={`Groups for ${user.name}`} options={groups.map((g) => ({ value: g.id, label: g.name }))} values={groupsDraft} onChange={setDraftGroups} disabled={!manageable || busy} />
            )}
            {manageable && groupsChanged && (
              <>
                {draftGrantsManagement && <WarningPanel>These groups give {user.name} management access. The change waits for a second person’s approval.</WarningPanel>}
                <div className={styles.actionsWrap}>
                  <Button size="sm" disabled={busy} onClick={() => patch({ groupIds: groupsDraft }).then(() => setDraftGroups(null))}>
                    {draftGrantsManagement ? 'Ask for approval' : 'Save groups'}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setDraftGroups(null)}>
                    Discard
                  </Button>
                </div>
              </>
            )}
          </div>
          <div className={styles.detailBlock}>
            <span className={styles.detailTitle}>Effective access</span>
            {user.status !== 'Active' ? (
              <Muted small>{user.status === 'Invited' ? 'Nothing until they accept the invitation.' : 'Nothing while suspended.'}</Muted>
            ) : (
              <PillRow>
                {summary.full.map((k) => (
                  <Pill key={k} tone="positive">
                    {MODULES[k].label} · full
                  </Pill>
                ))}
                {summary.read.map((k) => (
                  <Pill key={k} tone="neutral">
                    {MODULES[k].label} · read only
                  </Pill>
                ))}
                {summary.management !== 'none' && <Pill tone="warning">Management · {summary.management === 'full' ? 'full' : 'read only'}</Pill>}
                {summary.full.length === 0 && summary.read.length === 0 && summary.management === 'none' && (
                  <Muted small>{SHIPPED_MODULES.length === 0 ? 'No product module has shipped yet.' : 'No modules. Add them to a group.'}</Muted>
                )}
              </PillRow>
            )}
            <Muted small>Computed from their groups’ roles and the modules the organization has.</Muted>
          </div>
          {manageable && (
            <div className={styles.detailBlock}>
              <span className={styles.detailTitle}>Actions</span>
              <div className={styles.actionsWrap}>
                {user.status === 'Active' && !isSelf && (
                  <ConfirmButton label="Suspend" tone="critical" disabled={busy} message={`${user.name} loses access immediately, is signed out everywhere and gets an email. You can restore them later.`} confirmLabel="Suspend" onConfirm={() => patch({ status: 'Suspended' })} />
                )}
                {user.status === 'Suspended' && (
                  <Button variant="secondary" size="sm" disabled={busy} onClick={() => patch({ status: 'Active' })}>
                    Restore
                  </Button>
                )}
                {user.status === 'Invited' && (
                  <>
                    <Button variant="secondary" size="sm" disabled={busy} onClick={() => run(() => api(`/org/users/${user.id}/resend${q}`, { method: 'POST' }))}>
                      Resend invitation
                    </Button>
                    <ConfirmButton label="Revoke invitation" tone="critical" disabled={busy} message={`The invitation for ${user.email} stops working and the seat is freed. You can invite them again later.`} confirmLabel="Revoke" onConfirm={() => run(() => api(`/org/users/${user.id}${q}`, { method: 'DELETE' }))} />
                  </>
                )}
                {user.status === 'Active' && !tenant.ssoLabel && (
                  <ConfirmButton label="Reset second factor" disabled={busy} message={`Use this when ${user.name} lost their authenticator. Their sessions end now and they get an email; at the next sign-in they set up a new one.`} confirmLabel="Reset" onConfirm={() => run(() => api(`/org/users/${user.id}/reset-mfa${q}`, { method: 'POST' }))} />
                )}
                {user.status === 'Active' && !isSelf && (
                  <Button variant="secondary" size="sm" disabled={busy} onClick={() => run(() => api(`/org/users/${user.id}/sign-out${q}`, { method: 'POST' }))}>
                    Sign out everywhere
                  </Button>
                )}
              </div>
            </div>
          )}
          <div className={styles.detailBlock}>
            <span className={styles.detailTitle}>Recent activity</span>
            {activity.length === 0 && <Muted small>Nothing recorded for this person yet.</Muted>}
            {activity.map((a: AuditEntry) => (
              <div key={a.id} className={styles.activityItem}>
                {a.summary} · {when(a.at)}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
